import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";

@Component({
  standalone: false,
  selector: "app-file-rename-modal",
  templateUrl: "./file-rename-modal.component.html",
  styleUrls: ["./file-rename-modal.component.scss"]
})
export class FileRenameModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() fileName = "";

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() renamed = new EventEmitter<string>();

  @ViewChild("nameInput") nameInput?: ElementRef<HTMLInputElement>;

  editingValue = "";
  submitting = false;
  errorKey: string | null = null;

  // Blocks path separators/traversal and filesystem-reserved characters from
  // reaching the backend rename endpoint, which trusts this value as-is.
  private static readonly ILLEGAL_CHARS_PATTERN = /[/\\<>:"|?*\x00-\x1f]/;
  private static readonly MAX_NAME_LENGTH = 150;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the editable value every time the modal transitions to open.
    // Without this, cancelling after edits and reopening the same file would
    // show the previously typed (unsaved) text instead of the current filename.
    if (changes["isOpen"] && this.isOpen) {
      this.editingValue = this.stripExtension(this.fileName);
      this.submitting = false;
      this.errorKey = null;
      setTimeout(() => {
        const el = this.nameInput?.nativeElement;
        if (el) {
          el.focus();
          el.select();
        }
      });
    }
  }

  confirm(): void {
    if (this.submitting) return;
    const trimmed = (this.editingValue || "").trim();
    if (!trimmed) return;
    if (trimmed.length > FileRenameModalComponent.MAX_NAME_LENGTH) {
      this.errorKey = "uploads.File_Name_Too_Long";
      return;
    }
    if (trimmed === "." || trimmed === ".." || FileRenameModalComponent.ILLEGAL_CHARS_PATTERN.test(trimmed)) {
      this.errorKey = "uploads.File_Name_Invalid_Characters";
      return;
    }
    this.errorKey = null;
    const extension = this.getExtension(this.fileName);
    const finalName = trimmed + extension;
    if (finalName === this.fileName) {
      this.close();
      return;
    }
    this.submitting = true;
    this.renamed.emit(finalName);
    this.close();
  }

  close(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  private stripExtension(name: string): string {
    if (!name) return "";
    const dot = name.lastIndexOf(".");
    return dot !== -1 ? name.substring(0, dot) : name;
  }

  private getExtension(name: string): string {
    if (!name) return "";
    const dot = name.lastIndexOf(".");
    return dot !== -1 ? name.substring(dot) : "";
  }
}
